import { FC, useState } from 'react'
import { useMutation } from '~/shared/hooks/query'
import {
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormControl,
} from '@mui/material'
import { FFModal } from '~/shared/ui'
import { physicianPracticingStates } from '@fireflyhealth/api'
import type { StatesListResponse } from '@fireflyhealth/api'
import { useDispatch } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'
import { updateProviderPracticingStates } from '~/app/store/actions/providers'
import { Provider } from '~/entities/provider'
import { haveChanged } from '~/shared/lib/haveChanged'
import { required } from '~/shared/lib/required'

interface Props {
  stateOptions: StatesListResponse
  provider: Provider | null
  loading: boolean
  handleCancel: () => void
}

// Which of the options the provider currently practices in, keyed by state id.
const practicingStateSelection = (provider: Provider, stateOptions: StatesListResponse) => {
  const practicingStates = provider.provider_fields?.practicing_states ?? []
  const selection: Record<string, boolean> = {}
  stateOptions.forEach(state => {
    selection[state.id] = practicingStates.some(practicingState => practicingState.id === state.id)
  })
  return selection
}

const EditPracticingStatesModal: FC<Props> = (props: Props) => {
  const [selectedStates, setSelectedStates] = useState<Record<string, boolean>>({})
  const dispatch = useDispatch()

  // The selection resets to the provider's current states whenever the provider or options change.
  const selectionSource = [props.stateOptions, props.loading, props.provider]
  const [selectedFrom, setSelectedFrom] = useState<unknown[]>()
  if (haveChanged(selectedFrom, selectionSource)) {
    setSelectedFrom(selectionSource)
    if (props.provider && !props.loading && props.stateOptions) {
      setSelectedStates(practicingStateSelection(props.provider, props.stateOptions))
    }
  }

  const handleSelection = (stateId: number, isChecked: boolean) => {
    setSelectedStates({
      ...selectedStates,
      [stateId]: isChecked,
    })
  }

  const updatePracticingStatesMutation = useMutation(
    async () => {
      const { data } = await physicianPracticingStates.partialUpdate(
        {
          id: required(
            props.provider?.provider_fields?.physician_id,
            "the provider's physician id",
          ),
        },
        {
          data: {
            practicing_state_ids: Object.keys(selectedStates)
              .filter(stateId => !!selectedStates[stateId])
              .map(Number),
          },
        },
      )
      return data
    },
    {
      onSuccess: response => {
        dispatch(
          updateProviderPracticingStates(
            required(props.provider, 'a provider').id,
            response.practicing_states,
          ),
        )
        dispatch(queueNotification({ variant: 'success', message: 'Updated Practicing States' }))
        props.handleCancel()
      },
    },
    { error: 'Failed to update Practicing States' },
  )

  if (props.loading || !props.provider?.provider_fields?.physician_id) return null

  const nonTerritorialStates = props.stateOptions.filter(state => !state.territory)
  const easternStates = nonTerritorialStates.filter(state => state.time_zone_region === 'east')
  const westernStates = nonTerritorialStates.filter(state => state.time_zone_region === 'west')
  return (
    <FFModal
      open={true}
      header={
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" align="left">
            {`Edit Licenses for ${props.provider.first_name} ${props.provider.last_name}`}
          </Typography>
        </Box>
      }
      footer={
        <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="secondary"
            disabled={updatePracticingStatesMutation.isPending}
            onClick={() => props.handleCancel()}
          >
            Cancel
          </Button>
          <Box sx={{ ml: 1 }}>
            <Button
              color="primary"
              disabled={updatePracticingStatesMutation.isPending}
              onClick={() => updatePracticingStatesMutation.mutate()}
            >
              Save
            </Button>
          </Box>
        </Box>
      }
    >
      <Box sx={{ m: 2 }}>
        <Box sx={{ mt: 2 }}>
          <FormControl component="fieldset" variant="standard" fullWidth>
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
              Eastern Practicing Licenses
            </Typography>
            <Box sx={{ display: 'flex', paddingLeft: 3, paddingTop: 1 }}>
              <FormGroup style={{ flex: 1 }}>
                {easternStates
                  .filter((state, index) => index < easternStates.length / 2)
                  .map(state => {
                    return (
                      <FormControlLabel
                        key={state.id}
                        control={
                          <Checkbox
                            checked={!!selectedStates[state.id]}
                            onChange={e => handleSelection(state.id, e.target.checked)}
                            name={state.abbreviation}
                          />
                        }
                        label={`${state.name} (${state.abbreviation})`}
                      />
                    )
                  })}
              </FormGroup>
              <FormGroup style={{ flex: 1 }}>
                {easternStates
                  .filter((state, index) => index >= easternStates.length / 2)
                  .map(state => {
                    return (
                      <FormControlLabel
                        key={state.id}
                        control={
                          <Checkbox
                            checked={!!selectedStates[state.id]}
                            onChange={e => handleSelection(state.id, e.target.checked)}
                            name={state.abbreviation}
                          />
                        }
                        label={`${state.name} (${state.abbreviation})`}
                      />
                    )
                  })}
              </FormGroup>
            </Box>
          </FormControl>
          <FormControl component="fieldset" variant="standard" fullWidth style={{ marginTop: 20 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
              Western Practicing Licenses
            </Typography>
            <Box sx={{ display: 'flex', paddingLeft: 3, paddingTop: 1 }}>
              <FormGroup style={{ flex: 1 }}>
                {westernStates
                  .filter((state, index) => index < westernStates.length / 2)
                  .map(state => {
                    return (
                      <FormControlLabel
                        key={state.id}
                        control={
                          <Checkbox
                            checked={!!selectedStates[state.id]}
                            onChange={e => handleSelection(state.id, e.target.checked)}
                            name={state.abbreviation}
                          />
                        }
                        label={`${state.name} (${state.abbreviation})`}
                      />
                    )
                  })}
              </FormGroup>
              <FormGroup style={{ flex: 1 }}>
                {westernStates
                  .filter((state, index) => index >= westernStates.length / 2)
                  .map(state => {
                    return (
                      <FormControlLabel
                        key={state.id}
                        control={
                          <Checkbox
                            checked={!!selectedStates[state.id]}
                            onChange={e => handleSelection(state.id, e.target.checked)}
                            name={state.abbreviation}
                          />
                        }
                        label={`${state.name} (${state.abbreviation})`}
                      />
                    )
                  })}
              </FormGroup>
            </Box>
          </FormControl>
        </Box>
      </Box>
    </FFModal>
  )
}

export default EditPracticingStatesModal
